import { useEffect, useState } from "react";
import { Download, X, Share, Smartphone } from "lucide-react";
import { useInstall } from "@/contexts/InstallContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const LS_KEY = "cafecko-install-dismissed";

export default function InstallPrompt() {
  const { canPrompt, isIos, isStandalone, promptInstall, instructionsOpen, closeInstructions } = useInstall();
  const [bannerShown, setBannerShown] = useState(false);

  // Auto-banner: show once on first load if installable & not dismissed
  useEffect(() => {
    if (isStandalone) return;
    if (localStorage.getItem(LS_KEY)) return;
    if (!canPrompt) return;
    const t = setTimeout(() => setBannerShown(true), 1500);
    return () => clearTimeout(t);
  }, [canPrompt, isStandalone]);

  const dismissBanner = () => {
    localStorage.setItem(LS_KEY, "1");
    setBannerShown(false);
  };

  const handleAccept = async () => {
    const ok = await promptInstall();
    if (ok) setBannerShown(false);
    else dismissBanner();
  };

  return (
    <>
      {bannerShown && !isStandalone && (
        <div
          data-testid="pwa-install-prompt"
          className="fixed bottom-3 left-3 right-3 sm:left-auto sm:bottom-6 sm:right-6 sm:w-[360px] z-40 bg-white border border-[#E5E1D8] shadow-xl rounded-xl p-4 flex items-start gap-3"
          style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="w-10 h-10 rounded-lg bg-[#3A2D27] text-[#D4A373] grid place-items-center shrink-0">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm" style={{ fontFamily: "Outfit" }}>
              Přidat Caféčko na plochu
            </div>
            {isIos ? (
              <div className="text-xs text-[#57534E] mt-1 leading-relaxed">
                Klepněte na <Share className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5" /> (Sdílet) v Safari → <strong>Přidat na plochu</strong>.
              </div>
            ) : (
              <div className="text-xs text-[#57534E] mt-1">
                Spouštějte jedním klepnutím, bez prohlížečové lišty.
              </div>
            )}
            {!isIos && (
              <button
                data-testid="pwa-install-btn"
                onClick={handleAccept}
                className="mt-2.5 px-3 py-1.5 rounded-md bg-[#3A2D27] text-white text-sm font-medium hover:opacity-90"
              >
                Nainstalovat
              </button>
            )}
          </div>
          <button
            data-testid="pwa-install-dismiss"
            onClick={dismissBanner}
            className="p-1.5 rounded-md text-[#57534E] hover:bg-[#F9F6F0] shrink-0"
            title="Zavřít"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Instructions dialog (vyvolaný tlačítkem v menu) */}
      <Dialog open={instructionsOpen} onOpenChange={(o) => !o && closeInstructions()}>
        <DialogContent className="sm:max-w-md bg-white border-[#E5E1D8]" data-testid="install-instructions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Smartphone className="w-5 h-5" /> Přidat na plochu
            </DialogTitle>
            <DialogDescription>
              Postup podle typu zařízení a prohlížeče.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <section>
              <div className="label-eyebrow mb-2">iPhone / iPad (Safari)</div>
              <ol className="list-decimal pl-5 space-y-1 text-[#1C1917]">
                <li>Klepněte dole na ikonu <Share className="inline w-4 h-4 mx-0.5 -mt-0.5 text-[#3A2D27]" /> (Sdílet)</li>
                <li>Vyberte <strong>Přidat na plochu</strong></li>
                <li>Potvrďte <strong>Přidat</strong> v pravém horním rohu</li>
              </ol>
            </section>
            <section>
              <div className="label-eyebrow mb-2">Android (Chrome / Edge)</div>
              <ol className="list-decimal pl-5 space-y-1 text-[#1C1917]">
                <li>Otevřete menu (tři tečky vpravo nahoře)</li>
                <li>Vyberte <strong>Nainstalovat aplikaci</strong> nebo <strong>Přidat na plochu</strong></li>
                <li>Potvrďte instalaci</li>
              </ol>
            </section>
            <section>
              <div className="label-eyebrow mb-2">Desktop (Chrome / Edge)</div>
              <ol className="list-decimal pl-5 space-y-1 text-[#1C1917]">
                <li>V adresním řádku klepněte na ikonu <Download className="inline w-4 h-4 mx-0.5 -mt-0.5 text-[#3A2D27]" /></li>
                <li>Případně menu → <strong>Nainstalovat Caféčko</strong></li>
              </ol>
            </section>
            <p className="text-xs text-[#57534E] border-t border-[#E5E1D8] pt-3">
              Po instalaci se Caféčko objeví na ploše/v menu aplikací a funguje jako nativní app — bez prohlížečové lišty.
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={closeInstructions}
              className="btn-primary"
            >
              Rozumím
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
